/**
 * Unified formatting functions for center pivot display.
 *
 * These formatters provide consistent text output for pivot metrics
 * across all display contexts (text displays, map markers, control panels).
 */
import type { PivotDirection } from "@/constants/base"

import {
  formatDurationMs,
  formatHoursPerRevolution,
  getTimeToNextStopMs,
  hoursPerRevolutionToSpeedPercent,
  type PivotActivityMetrics,
} from "./device-activity-utils"

/**
 * Format pivot speed as a human-readable string.
 * Returns format like "3h 45m / rev" or null if no valid speed.
 */
export function formatPivotSpeed(
  metrics: PivotActivityMetrics,
): string | null {
  const formatted = formatHoursPerRevolution(metrics.hoursPerRevolution)
  return formatted ? `${formatted} / rev` : null
}

/**
 * Format pivot direction as a human-readable string.
 * Returns "Clockwise", "Counter-clockwise", or null.
 */
export function formatPivotDirection(
  direction: PivotDirection | null | undefined,
): string | null {
  if (direction === "pivot_clockwise") {
    return "Clockwise"
  }
  if (direction === "pivot_counterclockwise") {
    return "Counter-clockwise"
  }
  return null
}

const COMPASS_POINTS_8 = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"] as const

/**
 * Return the 8-point compass label ("N", "NE", ..., "NW") for an angle in
 * degrees where 0°/360° = North and values increase clockwise.
 */
export function cardinalDirection8(degrees: number): string {
  const normalized = ((degrees % 360) + 360) % 360
  const index = Math.round(normalized / 45) % 8
  return COMPASS_POINTS_8[index]!
}

/**
 * Format pivot heading as a degree string with cardinal direction.
 * Returns format like "45° NE" or null if no valid heading.
 */
export function formatPivotHeading(
  degrees: number | null | undefined,
): string | null {
  if (typeof degrees !== "number" || !Number.isFinite(degrees)) {
    return null
  }
  const normalized = ((degrees % 360) + 360) % 360
  return `${Math.round(normalized)}° ${cardinalDirection8(normalized)}`
}

/**
 * Format time to next stop as a human-readable duration.
 * Returns format like "2h 15m" or null if no valid timestamp.
 */
export function formatTimeToStop(
  nextStopExpectedTimestamp: string | null | undefined,
): string | null {
  const ms = getTimeToNextStopMs(nextStopExpectedTimestamp)
  return formatDurationMs(ms)
}

/**
 * Format percent complete as a percentage string.
 * Returns format like "75%" or null if no valid value.
 */
export function formatPercentComplete(
  percentComplete: number | null | undefined,
): string | null {
  if (typeof percentComplete !== "number" || !Number.isFinite(percentComplete)) {
    return null
  }
  return `${Math.round(percentComplete)}%`
}

/**
 * Format speed percentage (0-100) as a string.
 * Returns format like "50%" or null if no valid value.
 */
export function formatSpeedPercent(
  speedPercent: number | null | undefined,
): string | null {
  if (typeof speedPercent !== "number" || !Number.isFinite(speedPercent)) {
    return null
  }
  return `${Math.round(speedPercent)}%`
}

/**
 * Get all formatted pivot metrics in a single object.
 * Useful for display components that need multiple formatted values.
 */
export interface FormattedPivotMetrics {
  /** Speed in human-readable format (e.g., "3h 45m / rev") */
  speed: string | null
  /** Speed as percentage (e.g., "50%") */
  speedPercent: string | null
  /** Speed percentage value (0-100) */
  speedPercentValue: number | null
  /** Direction text (e.g., "Clockwise") */
  direction: string | null
  /** Heading in degrees (e.g., "45°") */
  heading: string | null
  /** Time remaining to next stop (e.g., "2h 15m") */
  timeToStop: string | null
  /** Progress to next stop (e.g., "75%") */
  percentComplete: string | null
  /** Progress to next stop as numeric value (0-100) */
  percentCompleteValue: number | null
  /** Next stop label if available */
  nextStopLabel: string | null
  /** Combined next stop display (label + time) */
  nextStopDisplay: string | null
}

/**
 * Get all formatted pivot metrics from activity data.
 */
export function getFormattedPivotMetrics(
  metrics: PivotActivityMetrics,
): FormattedPivotMetrics {
  const speed = formatPivotSpeed(metrics)
  const speedPercentValue = hoursPerRevolutionToSpeedPercent(metrics.hoursPerRevolution)
  const speedPercent = formatSpeedPercent(speedPercentValue)
  const direction = formatPivotDirection(metrics.direction)
  const heading = formatPivotHeading(metrics.currentHeadingDegrees)
  const timeToStop = formatTimeToStop(metrics.nextStopExpectedTimestamp)
  const percentCompleteValue = typeof metrics.percentComplete === "number" && Number.isFinite(metrics.percentComplete)
    ? metrics.percentComplete
    : null
  const percentComplete = formatPercentComplete(metrics.percentComplete)
  const nextStopLabel = metrics.nextStopLabel

  // Build combined next stop display
  let nextStopDisplay: string | null = null
  if (nextStopLabel && timeToStop) {
    nextStopDisplay = `${nextStopLabel} (${timeToStop})`
  } else if (timeToStop) {
    nextStopDisplay = timeToStop
  } else if (nextStopLabel) {
    nextStopDisplay = nextStopLabel
  }

  return {
    speed,
    speedPercent,
    speedPercentValue,
    direction,
    heading,
    timeToStop,
    percentComplete,
    percentCompleteValue,
    nextStopLabel,
    nextStopDisplay,
  }
}
